import { Field, getIn, useFormikContext } from "formik";
import { useContext, useEffect, useMemo } from "react";
import { useAppSelector, useGetAppLanguage } from "@app/hooks";
import type { ModuleFull } from "./forestManagementTypes";
import type { ForestRadioGroupInputGroup } from "@typedefs/modulesInterfaces";
import ModuleContext from "../../ModuleContext";

import { selectIsFetchingData } from "@features/builder/builderSlice";
import {
  useGetForestConditionTypesQuery,
  useGetForestTypesQuery,
} from "@hooks/index";
import FormRow from "@components/input/formRow/FormRow";
import Skeleton from "react-loading-skeleton";
import ForestContext from "@contexts/ForestContext";
import TranslatableText from "@components/translations/TranslatableText";

// Constants for forest types and conditions
const FOREST_TYPE_NAMES = {
  NATURAL: "Natural",
  PLANTATION: "Plantation",
} as const;

const FOREST_CONDITION_NAMES = {
  PRIMARY: "Primary",
  SECONDARY: "Secondary",
} as const;

// Radio option values
const RADIO_VALUES = {
  NATURAL_PRIMARY: 1,
  NATURAL_SECONDARY: 2,
  PLANTATION_SECONDARY: 3,
} as const;

// Custom hook for forest data management
const useForestData = () => {
  const { data: forestTypes, isLoading: isForestTypeLoading } = useGetForestTypesQuery();
  const { data: forestCondTypes, isLoading: isForestCondTypeLoading } = useGetForestConditionTypesQuery();
  const forestContext = useContext(ForestContext);

  useEffect(() => {
    if (forestTypes) {
      forestContext?.setForestTypes(forestTypes);
    }
  }, [forestContext, forestTypes]);

  return {
    forestTypes,
    forestCondTypes,
    isLoading: isForestTypeLoading || isForestCondTypeLoading,
  };
};

// Custom hook for forest type mapping
const useForestTypeMapping = (forestTypes: any[], forestCondTypes: any[]) => {
  return useMemo(() => {
    const naturalType = forestTypes?.find(type => type.name_en?.startsWith(FOREST_TYPE_NAMES.NATURAL));
    const plantationType = forestTypes?.find(type => type.name_en?.startsWith(FOREST_TYPE_NAMES.PLANTATION));
    const primaryCondition = forestCondTypes?.find(type => type.name_en?.startsWith(FOREST_CONDITION_NAMES.PRIMARY));
    const secondaryCondition = forestCondTypes?.find(type => type.name_en?.startsWith(FOREST_CONDITION_NAMES.SECONDARY));

    return {
      naturalType,
      plantationType,
      primaryCondition,
      secondaryCondition,
    };
  }, [forestTypes, forestCondTypes]);
};

// Custom hook for radio state management
const useRadioState = (forestTypeMapping: any, typeOfForestValue: number, forestConditionTypeValue: number) => {
  return useMemo(() => {
    const { naturalType, plantationType, primaryCondition, secondaryCondition } = forestTypeMapping;

    if (typeOfForestValue === naturalType?.id && forestConditionTypeValue === primaryCondition?.id) {
      return RADIO_VALUES.NATURAL_PRIMARY;
    }
    if (typeOfForestValue === naturalType?.id && forestConditionTypeValue === secondaryCondition?.id) {
      return RADIO_VALUES.NATURAL_SECONDARY;
    }
    if (typeOfForestValue === plantationType?.id && forestConditionTypeValue === secondaryCondition?.id) {
      return RADIO_VALUES.PLANTATION_SECONDARY;
    }
    return null;
  }, [forestTypeMapping, typeOfForestValue, forestConditionTypeValue]);
};

// Custom hook for radio options
const useRadioOptions = (forestTypeMapping: any, apiLanguageField: string) => {
  return useMemo(() => {
    const { primaryCondition, secondaryCondition } = forestTypeMapping;

    if (!primaryCondition || !secondaryCondition) return [];

    return [
      {
        label: primaryCondition[apiLanguageField],
        value: RADIO_VALUES.NATURAL_PRIMARY,
      },
      {
        label: secondaryCondition[apiLanguageField],
        value: RADIO_VALUES.NATURAL_SECONDARY,
      },
      {
        label: "Forest plantation (high density)",
        value: RADIO_VALUES.PLANTATION_SECONDARY,
      },
    ];
  }, [forestTypeMapping, apiLanguageField]);
};

const TypeOfForestRadioGroup = ({ 
  inputGroup, 
  disabled 
}: { 
  inputGroup: ForestRadioGroupInputGroup; 
  disabled: boolean;
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<ModuleFull>();
  const { apiLanguageField } = useGetAppLanguage();
  const isFetchingData = useAppSelector(selectIsFetchingData);
  const { belongsToLuc } = useContext(ModuleContext);

  // Get field names from input group
  const typeOfForestInputName = inputGroup.forestTypeInputName;
  const forestConditionTypeInputName = inputGroup.forestConditionTypeInputName;

  // Get current values
  const typeOfForestValue = useMemo(() => getIn(values, typeOfForestInputName), [values, typeOfForestInputName]);
  const forestConditionTypeValue = useMemo(() => getIn(values, forestConditionTypeInputName), [values, forestConditionTypeInputName]);

  // Get forest data
  const { forestTypes, forestCondTypes, isLoading } = useForestData();

  // Get forest type mapping
  const forestTypeMapping = useForestTypeMapping(forestTypes || [], forestCondTypes || []);

  // Get current radio selection
  const selectedRadio = useRadioState(forestTypeMapping, typeOfForestValue, forestConditionTypeValue);

  const shouldShowPrimaryForestWarningContext = useMemo(() => {
    if (!belongsToLuc) return false;
    return belongsToLuc.is_start === false && !!(belongsToLuc.is_w || belongsToLuc.is_wo);
  }, [belongsToLuc]);

  const shouldShowPrimaryForestWarning = useMemo(() => {
    return (
      shouldShowPrimaryForestWarningContext &&
      selectedRadio === RADIO_VALUES.NATURAL_PRIMARY
    );
  }, [selectedRadio, shouldShowPrimaryForestWarningContext]);

  // Get radio options
  const options = useRadioOptions(forestTypeMapping, apiLanguageField);

  // Handle radio change
  const handleRadioChange = (clickedRadio: number) => {
    const { naturalType, plantationType, primaryCondition, secondaryCondition } = forestTypeMapping;

    let typeValue = null;
    let conditionValue = null;

    switch (clickedRadio) {
      case RADIO_VALUES.NATURAL_PRIMARY:
        typeValue = naturalType?.id;
        conditionValue = primaryCondition?.id;
        break;
      case RADIO_VALUES.NATURAL_SECONDARY:
        typeValue = naturalType?.id;
        conditionValue = secondaryCondition?.id;
        break;
      case RADIO_VALUES.PLANTATION_SECONDARY:
        typeValue = plantationType?.id;
        conditionValue = secondaryCondition?.id;
        break;
    }

    setFieldValue(typeOfForestInputName, typeValue);
    setFieldValue(forestConditionTypeInputName, conditionValue);
  };

  // Get validation error
  const validationError = useMemo(() => {
    const hasError = getIn(errors, typeOfForestInputName) && getIn(touched, typeOfForestInputName);
    return hasError ? getIn(errors, typeOfForestInputName) : undefined;
  }, [errors, touched, typeOfForestInputName]);

  // Show loading state
  if (isFetchingData || isLoading || !options.length) {
    return (
      <FormRow style={{ height: "fit-content" }}>
        <Skeleton height="100%" count={3} />
      </FormRow>
    );
  }

  return (
    <FormRow
      style={{ height: "fit-content" }}
      error={validationError}
    >
      <div
        role="group"
        aria-labelledby="Type of forest"
        className="d-flex f-column"
        style={{ width: "80%" }}
      >
        {options.map((option, index) => (
          <label
            className="clickable d-flex align-items-center pb-1"
            key={index}
            onClick={() => {
              if (!disabled) handleRadioChange(option.value);
            }}
          >
            <Field
              type="radio"
              value={option.value}
              checked={selectedRadio === option.value}
              onClick={() => {
                if (!disabled) handleRadioChange(option.value);
              }}
              name="not_used"
              disabled={disabled}
            />
            <span className="ps-1 fs-12 ff-medium">
              {option.value === RADIO_VALUES.PLANTATION_SECONDARY ? (
                <TranslatableText translationKey="module.forest_plantation" />
              ) : (
                option.label
              )}
            </span>
          </label>
        ))}
        {shouldShowPrimaryForestWarning && (
          <div
            className="tiertwo-tooltip fs-12 mt-1"
            style={{
              marginLeft: 0,
              marginTop: "0.25rem",
              boxShadow: "none",
              width: "100%",
            }}
          >
            <TranslatableText translationKey="module.forest_primary_luc_warning" />
          </div>
        )}
      </div>
    </FormRow>
  );
};

export default TypeOfForestRadioGroup;